import type { QueryClient } from '@tanstack/react-query';
import type { AuthUserProfile } from '../src/stores/auth-store';

/** Stable actor boundary for shared-shell React Query cache resets. */
export type QueryAuthIdentity = {
  userId: string;
  tenantId: string;
  role: string;
};

export function buildQueryAuthIdentity(user: AuthUserProfile | null): QueryAuthIdentity | null {
  if (!user) return null;
  return {
    userId: user.id,
    tenantId: user.tenantId,
    role: user.role,
  };
}

export function queryAuthIdentitiesEqual(a: QueryAuthIdentity, b: QueryAuthIdentity): boolean {
  return a.userId === b.userId && a.tenantId === b.tenantId && a.role === b.role;
}

export function queryAuthIdentityChanged(
  previous: AuthUserProfile | null,
  next: AuthUserProfile | null,
): boolean {
  const previousIdentity = buildQueryAuthIdentity(previous);
  const nextIdentity = buildQueryAuthIdentity(next);
  if (!previousIdentity && !nextIdentity) return false;
  if (!previousIdentity || !nextIdentity) return true;
  return !queryAuthIdentitiesEqual(previousIdentity, nextIdentity);
}

export function formatQueryAuthRealmKey(identity: QueryAuthIdentity): string {
  return `${identity.userId}:${identity.tenantId}:${identity.role}`;
}

/**
 * Decide whether the shared QueryClient must rotate to a fresh render realm.
 *
 * Transient auth nulls (retryable blips, other-tab handoff) must not rotate.
 * First boot/login (null -> actor) must preserve any legitimate seeded cache.
 */
export function shouldRotateQueryAuthRealm(
  committedNonNullIdentity: QueryAuthIdentity | null,
  nextIdentity: QueryAuthIdentity | null,
): boolean {
  if (nextIdentity === null) return false;
  if (committedNonNullIdentity === null) return false;
  return !queryAuthIdentitiesEqual(committedNonNullIdentity, nextIdentity);
}

/** @deprecated Use shouldRotateQueryAuthRealm — kept for focused helper tests. */
export const shouldResetQueryCacheForIdentityChange = shouldRotateQueryAuthRealm;

export function abandonQueryClientRealm(queryClient: QueryClient): void {
  void queryClient.cancelQueries({ type: 'all' });
  queryClient.getMutationCache().clear();
  queryClient.clear();
}

/** @deprecated Use abandonQueryClientRealm — kept for transitional imports. */
export const purgeQueryClientForAuthBoundary = abandonQueryClientRealm;
